import {
  dateUtils,
  type Except,
  notEmpty,
  type ValidatedKey,
} from '@festina-life/core'
import { useField } from 'formik'

import { DateInput } from '../../forms/dateInput/DateInput'
import type { RawDateInputProps } from '../../forms/dateInput/RawDateInput'
import { useFormikErrorWithState } from '../../hooks/useFormikErrorWithState'
import { useFormikIsDisabled } from '../../hooks/useFormikIsDisabled'
import { useFormikIsRequired } from '../../hooks/useFormikIsRequired'
import { useExistingValuesContext } from '../../theme/existingValuesContext'
import {
  handleDefaultValueClick,
  handleExistingValueClick,
} from '../formikUtils/formikUtils'
import { getWrapperProps } from '../formikUtils/getWrapperProps'
import { useDateValueDifferenceCheck } from '../formikUtils/useDateValueDifferenceCheck'
import type { FormikInputPropsForWrapper } from '../types'
import { withFormikInputDiffing } from './withFormikInputDiffing'

export type FormikDateInputProps = Except<
  RawDateInputProps,
  'defaultValue' | 'onChange' | 'onBlur'
> & {
  name: ValidatedKey
  defaultValue?: string
  editable?: boolean
  existingValue?: string
  showExistingValue?: boolean
  onBlur?: (value: string | undefined) => void
} & FormikInputPropsForWrapper

export const FormikDateInputInternal = ({
  name,
  isRequired,
  label,
  description,
  defaultValue,
  validationText,
  onDefaultValueClick,
  disabled,
  onBlur,
  hasRightColumnAlways,
  showExistingValue,
  editable = true,
  existingValueConfig,
  className,
  isTableCell,
  ...restProps
}: FormikDateInputProps) => {
  const [field, meta, { setValue }] = useField({ name })
  const { isInError, error } = useFormikErrorWithState(meta)
  const isFieldDisabled = useFormikIsDisabled(disabled)
  const existingValue = useExistingValuesContext()?.getExistingValue?.(
    name,
  ) as string

  const handleOnBlur = (newValue: string) => {
    // We can avoid creating a fake event here.
    // Formik's onBlur only needs to look at the target's name.
    field.onBlur({ target: { name } })
    onBlur?.(newValue)
  }

  const handleOnChange = (newValue: string) => {
    // We can avoid creating a fake event here.
    // Formik's onChange only needs to look at the target's name, value and type.
    // The specific type doesn't matter, as long as it is
    // something that won't cause Formik to modify the value.
    field.onChange({ target: { name, value: newValue, type: 'text' } })
  }

  const inputValueDifferenceFromExistingValue =
    useDateValueDifferenceCheck(name)

  const wrapperProps = getWrapperProps({
    isTableCell,
    label,
    existingValue: notEmpty(existingValue)
      ? dateUtils.dateAsStr(existingValue)
      : undefined,
    existingValueConfig,
    inputValueDifferenceFromExistingValue,
    commonWrapperProps: {
      isRequired: useFormikIsRequired(name, isRequired),
      description,
      validationText,
      defaultValue: notEmpty(defaultValue)
        ? dateUtils.dateAsStr(defaultValue)
        : undefined,
      onDefaultValueClick: () =>
        handleDefaultValueClick({
          defaultValue,
          onBlur,
          onDefaultValueClick,
        }),
      onExistingValueClick: () =>
        handleExistingValueClick({
          existingValue,
          setValue,
          onBlur,
        }),
      htmlFor: name,
      hasRightColumnAlways,
      error,
      className,
    },
  })

  return (
    <DateInput
      wrapperProps={wrapperProps}
      id={name}
      placeholder={
        (existingValue ?? defaultValue)
          ? dateUtils.dateAsStr(existingValue ?? defaultValue)
          : undefined
      }
      isInError={isInError}
      disabled={isFieldDisabled}
      editable={editable}
      min="0001-01-01"
      max={dateUtils.MAX_DATE_STRING}
      {...field}
      {...restProps}
      onBlur={handleOnBlur}
      onChange={handleOnChange}
    />
  )
}

export const FormikDateInput = withFormikInputDiffing(FormikDateInputInternal)
